import type { ReactElement } from 'react';
import { HeroCopy } from './hero-copy';
import type { HeroSlideCopy } from './hero-slide-item';

interface HeroSlideProps {
  /** 왼쪽 문구(배너 문구 → 연결 상품 순으로 고른 결과). null 이면 문구·버튼 없이 사진만 보여 준다. */
  readonly copy: HeroSlideCopy | null;
  /** 배경 사진 — admin 배너 사진만 쓴다(상품 대표사진·썸네일은 쓰지 않는다). 없으면 히어로의 흑연 판이 비친다. */
  readonly imageUrl: string | null;
  /** 첫 장만 바로 불러오고 나머지는 늦게 불러온다. */
  readonly isFirst: boolean;
}

/**
 * 히어로 슬라이드 한 장 — 전폭 배경 사진 위 왼쪽에 배너 문구. 사진은 장식이라 alt 를 비운다.
 * 넓은 화면에서는 사진을 키우거나 자르지 않고 화면 안에 다 담는다(남는 자리는 흐린 같은 사진으로 잇는다).
 */
export const HeroSlide = ({ copy, imageUrl, isFirst }: HeroSlideProps): ReactElement => (
  <div className="relative size-full overflow-hidden">
    {imageUrl !== null ? (
      <>
        {/* 바탕 — 같은 사진을 크게 흐려 깔아, 사진을 화면에 다 담고 남는 자리(주로 왼쪽 문구 자리)를 잇는다. */}
        <img
          src={imageUrl}
          alt=""
          aria-hidden
          draggable={false}
          loading={isFirst ? 'eager' : 'lazy'}
          className="absolute inset-0 hidden size-full scale-125 object-cover blur-2xl md:block"
        />
        {/* 사진 — 넓은 화면에서는 높이를 화면에 맞추고 원본 비율 그대로 오른쪽(상품 자리)에 붙인다. 더 키우지
            않으므로 상품이 과하게 커지지 않는다. 왼쪽 끝은 흐린 바탕으로 스며들게 지워 경계가 보이지 않게 한다.
            휴대폰 세로 화면에서는 그러면 너무 작아지므로 화면을 덮는다. */}
        <img
          src={imageUrl}
          alt=""
          draggable={false}
          loading={isFirst ? 'eager' : 'lazy'}
          fetchPriority={isFirst ? 'high' : undefined}
          className="absolute inset-0 size-full object-cover object-[70%_50%] md:inset-auto md:top-0 md:right-0 md:w-auto md:max-w-none md:[mask-image:linear-gradient(to_right,transparent,black_18%)]"
        />
        {/* 밝은 사진 위에서도 왼쪽 흰 글자가 읽히도록 왼쪽만 어둡게 깐다 — 문구가 없으면 사진을 그대로 둔다. */}
        {copy !== null ? (
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/15 to-transparent"
          />
        ) : null}
        {/* 사진 위에 얹은 흰 로고·메뉴가 밝은 사진에서도 읽히도록 위쪽도 옅게 어둡게 깐다. */}
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-black/40 to-transparent"
        />
      </>
    ) : null}

    {copy !== null ? (
      <div className="relative flex h-full items-center px-5 pt-28 sm:px-8 sm:pt-36 xl:px-14">
        <HeroCopy copy={copy} />
      </div>
    ) : null}
  </div>
);
